import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LABOR_COLS = [
  { key: 'employee',        label: 'Employee',        type: 'text',   width: 'w-44' },
  { key: 'hours',           label: 'Hours',           type: 'number', width: 'w-24' },
  { key: 'amount',          label: 'Amount ($)',      type: 'number', width: 'w-28' },
  { key: 'vehicle_expense', label: 'Vehicle ($)',     type: 'number', width: 'w-28' },
  { key: 'fuel_expense',    label: 'Fuel ($)',        type: 'number', width: 'w-28' },
  { key: 'date',            label: 'Date',            type: 'date',   width: 'w-40' },
];

const EXPENSE_COLS = [
  { key: 'expense', label: 'Expense', type: 'text',   width: 'w-52' },
  { key: 'date',    label: 'Date',    type: 'date',   width: 'w-40' },
  { key: 'memo',    label: 'Memo',    type: 'text',   width: 'flex-1' },
  { key: 'amount',  label: 'Amount ($)', type: 'number', width: 'w-28' },
];

// Notes import: legacy free-form date+time string in `created_on` is kept
// verbatim (e.g. "5/15/2026 10:57am") so it matches the legacy display.
const NOTES_COLS = [
  { key: 'created_by', label: 'Created By', type: 'text',     width: 'w-40' },
  { key: 'created_on', label: 'Created On', type: 'text',     width: 'w-48' },
  { key: 'text',       label: 'Notes',      type: 'text',     width: 'flex-1' },
  { key: 'urgent',     label: 'Urgent',     type: 'checkbox', width: 'w-20' },
];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isoToSlashDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${parseInt(m, 10)}/${String(d).padStart(2, '0')}/${y}`;
};

const ImportLaborExpenses = () => {
  const { user } = useAuth();
  const fileRef = useRef(null);

  const [kind, setKind] = useState('labor');
  const [files, setFiles] = useState([]); // queue of staged screenshots
  const [parsing, setParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState({ done: 0, total: 0 });
  const [committing, setCommitting] = useState(false);

  const [projectId, setProjectId] = useState('');
  const [projectExists, setProjectExists] = useState(null); // null | true | false
  const [rows, setRows] = useState([]);
  // Per-column user overrides for the totals footer row. `null` = use the
  // computed sum of `rows`; otherwise the user-typed string is shown verbatim.
  const [totalsOverride, setTotalsOverride] = useState({});
  const [history, setHistory] = useState([]);
  const [undoing, setUndoing] = useState(null); // batch_id being undone

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/ocr-import/history?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const { data } = await readResponse(res);
      setHistory(data.batches || []);
    } catch { /* ignore */ }
  };

  React.useEffect(() => {
    if (user?.is_admin) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  // Global paste listener so the user can just hit Ctrl/Cmd+V anywhere on the page.
  React.useEffect(() => {
    if (!user?.is_admin) return undefined;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      const added = [];
      for (const it of items) {
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const blob = it.getAsFile();
          if (!blob) continue;
          const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
          added.push(new File([blob], `pasted-${Date.now()}-${added.length}.${ext}`, { type: blob.type || 'image/png' }));
        }
      }
      if (added.length) {
        setFiles((prev) => [...prev, ...added]);
        toast.success(`Pasted ${added.length} screenshot${added.length === 1 ? '' : 's'} from clipboard.`);
        e.preventDefault();
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  if (!user?.is_admin) {
    return (
      <Layout>
        <div className="p-8 text-center text-red-600 font-medium" data-testid="ocr-admin-required">
          Admin access required.
        </div>
      </Layout>
    );
  }

  const reset = () => {
    setFiles([]);
    setRows([]);
    setTotalsOverride({});
    setProjectId('');
    setProjectExists(null);
    setParseProgress({ done: 0, total: 0 });
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onFileInputChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) {
      setFiles((prev) => [...prev, ...picked]);
    }
    // reset the native input so the same file can be re-selected later
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleKindChange = (k) => {
    if (k === kind) return;
    setKind(k);
    reset();
  };

  // Read a File as a naked base64 string (no data: prefix).
  const fileToBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const comma = String(result).indexOf(',');
      resolve(comma >= 0 ? String(result).slice(comma + 1) : String(result));
    };
    reader.onerror = () => reject(reader.error || new Error('File read failed'));
    reader.readAsDataURL(f);
  });

  const handleParse = async () => {
    if (files.length === 0) {
      toast.error('Add at least one screenshot first.');
      return;
    }
    setParsing(true);
    setParseProgress({ done: 0, total: files.length });
    const token = localStorage.getItem('token');
    const allRows = [];
    const projectGuesses = []; // list of {pid, exists}
    const detectedKinds = []; // what each screenshot ACTUALLY shows
    let failures = 0;

    // If the user already typed/locked a Project # before clicking Parse,
    // every screenshot's rows are committed to that project regardless of
    // what OCR detects on each individual screenshot. This is the common
    // workflow when importing several screenshots of the same job's Labor
    // Summary — slight OCR misreads on the project ID won't fork the import.
    const userLockedPid = projectId.trim();

    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      try {
        const image_base64 = await fileToBase64(f);
        const res = await fetch(`${API_URL}/api/admin/ocr-import/parse`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            kind,
            image_base64,
            content_type: f.type || 'image/png',
          }),
        });
        const { data } = await readResponse(res);
        if (!res.ok) throw new Error(data?.detail || 'Parse failed');
        if (data.project_id_guess) {
          projectGuesses.push({ pid: data.project_id_guess, exists: !!data.project_exists });
        }
        if (data.detected_kind) {
          detectedKinds.push(data.detected_kind);
        }
        for (const r of data.rows || []) {
          // Labor rows don't come with dates from OCR — default each to today
          // (still per-row editable).
          if (kind === 'labor' && !r.date) {
            allRows.push({ ...r, date: todayISO() });
          } else {
            allRows.push({ ...r });
          }
        }
      } catch (e) {
        failures += 1;
        toast.error(`Screenshot ${i + 1} failed: ${e.message}`);
      } finally {
        setParseProgress({ done: i + 1, total: files.length });
      }
    }

    // Project # resolution:
    //   1. If the user already typed one before parsing, keep it (locked).
    //   2. Otherwise, pick the most common OCR guess.
    if (userLockedPid) {
      // Re-verify it still exists (in case user typed something new), but
      // never overwrite the user's choice with OCR results.
      checkProject(userLockedPid);
      const ocrDiffers = projectGuesses.some((g) => g.pid && g.pid !== userLockedPid);
      if (ocrDiffers) {
        const ocrPids = [...new Set(projectGuesses.map((g) => g.pid).filter(Boolean))];
        toast.info(`Using your locked Project # "${userLockedPid}". OCR also detected: ${ocrPids.join(', ')}.`);
      }
    } else {
      const uniqueGuesses = [...new Set(projectGuesses.map((g) => g.pid))];
      const chosen = projectGuesses[0]?.pid || '';
      setProjectId(chosen);
      setProjectExists(chosen ? projectGuesses[0].exists : null);
      if (uniqueGuesses.length > 1) {
        toast.warning(`Project # differed across screenshots: ${uniqueGuesses.join(', ')}. Using "${chosen}" — edit if needed.`);
      }
    }

    setRows((prev) => [...prev, ...allRows]);
    setTotalsOverride({});

    if (allRows.length === 0 && failures === 0) {
      // If the screenshots actually show a different kind of table, tell the
      // user — that's the common "No rows detected" cause when someone is on
      // the Labor tab but uploaded an Expenses screenshot (or vice versa).
      const wrongKinds = [...new Set(detectedKinds.filter((k) => k && k !== kind && k !== 'unknown'))];
      if (wrongKinds.length === 1) {
        const label = wrongKinds[0] === 'labor' ? 'Labor Summary'
          : wrongKinds[0] === 'expense' ? 'Project Expenses'
          : wrongKinds[0] === 'notes' ? 'Notes' : wrongKinds[0];
        toast.error(`Detected a "${label}" table, but you're on the "${kind === 'labor' ? 'Labor Summary' : kind === 'expense' ? 'Project Expenses' : 'Notes'}" tab. Switch the Type toggle above to "${label}" and try again.`, { duration: 8000 });
      } else if (wrongKinds.length > 1) {
        toast.error(`No ${kind === 'labor' ? 'labor' : kind === 'expense' ? 'expense' : 'note'} rows detected. The screenshots appear to show: ${wrongKinds.join(', ')}. Use one type at a time via the Type toggle.`, { duration: 8000 });
      } else {
        toast.warning('No rows detected. Try clearer screenshots.');
      }
    } else if (allRows.length > 0) {
      toast.success(`Parsed ${allRows.length} row(s) from ${files.length - failures} screenshot${files.length - failures === 1 ? '' : 's'}.`);
      // Even when we got some rows, surface a mismatch if any of the
      // uploaded screenshots looks like a different kind of table — that
      // batch would silently be dropped, so the user needs to know.
      const wrongKinds = [...new Set(detectedKinds.filter((k) => k && k !== kind && k !== 'unknown'))];
      if (wrongKinds.length > 0) {
        const labels = wrongKinds.map((k) => k === 'labor' ? 'Labor Summary' : k === 'expense' ? 'Project Expenses' : k === 'notes' ? 'Notes' : k);
        toast.warning(`Some screenshots appear to show a ${labels.join('/')} table instead — those rows were skipped. Switch the Type toggle to import them.`, { duration: 7000 });
      }
    }
    setFiles([]);
    setParsing(false);
  };

  const checkProject = async (pid) => {
    setProjectId(pid);
    if (!pid) {
      setProjectExists(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/projects/${encodeURIComponent(pid)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjectExists(res.ok);
    } catch {
      setProjectExists(false);
    }
  };

  const updateRow = (idx, key, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const addRow = () => {
    const blank = kind === 'labor'
      ? { employee: '', hours: 0, amount: 0, vehicle_expense: 0, fuel_expense: 0, date: todayISO() }
      : kind === 'expense'
      ? { expense: '', date: '', memo: '', amount: 0 }
      : { created_by: '', created_on: '', text: '', urgent: false };
    setRows((prev) => [...prev, blank]);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCommit = async () => {
    if (!projectId.trim()) {
      toast.error('Project # is required.');
      return;
    }
    if (rows.length === 0) {
      toast.error('No rows to commit.');
      return;
    }
    setCommitting(true);
    try {
      const token = localStorage.getItem('token');
      let url, body;
      if (kind === 'labor') {
        url = `${API_URL}/api/admin/ocr-import/commit-labor`;
        body = {
          project_id: projectId.trim(),
          rows: rows.map((r) => ({
            employee: String(r.employee || '').trim(),
            hours: parseFloat(r.hours) || 0,
            amount: parseFloat(r.amount) || 0,
            vehicle_expense: parseFloat(r.vehicle_expense) || 0,
            fuel_expense: parseFloat(r.fuel_expense) || 0,
            date: isoToSlashDate(r.date || todayISO()),
          })),
        };
      } else if (kind === 'expense') {
        url = `${API_URL}/api/admin/ocr-import/commit-expenses`;
        body = {
          project_id: projectId.trim(),
          rows: rows.map((r) => ({
            expense: String(r.expense || '').trim(),
            date: String(r.date || '').trim(),
            memo: String(r.memo || '').trim(),
            amount: parseFloat(r.amount) || 0,
          })),
        };
      } else {
        url = `${API_URL}/api/admin/ocr-import/commit-notes`;
        body = {
          project_id: projectId.trim(),
          rows: rows.map((r) => ({
            created_by: String(r.created_by || '').trim(),
            created_on: String(r.created_on || '').trim(),
            text: String(r.text || '').trim(),
            urgent: Boolean(r.urgent),
          })),
        };
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const { data } = await readResponse(res);
      if (!res.ok) {
        throw new Error(data?.detail || 'Commit failed');
      }
      if (kind === 'labor') {
        toast.success(`Added ${data.created} labor row(s) to ${projectId} (${data.employees_auto_created} new employee(s) auto-created).`);
      } else if (kind === 'expense') {
        toast.success(`Added ${data.added} expense row(s) to ${projectId}.`);
      } else {
        const replaced = data.replaced || 0;
        const replacedMsg = replaced > 0 ? ` (replaced ${replaced} existing duplicate${replaced === 1 ? '' : 's'})` : '';
        toast.success(`Added ${data.added} note(s) to ${projectId}${replacedMsg}.`);
      }
      reset();
      fetchHistory();
    } catch (e) {
      toast.error(`Commit failed: ${e.message}`);
    } finally {
      setCommitting(false);
    }
  };

  const handleUndo = async (batch) => {
    const ok = window.confirm(
      `Undo this import?\n\n${batch.kind === 'labor' ? 'Labor' : batch.kind === 'expense' ? 'Expenses' : 'Notes'} → ${batch.project_id} (${batch.row_count} row${batch.row_count === 1 ? '' : 's'})\n\nThis cannot be undone.`
    );
    if (!ok) return;
    setUndoing(batch.batch_id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/ocr-import/history/${batch.batch_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data } = await readResponse(res);
      if (!res.ok) throw new Error(data?.detail || 'Undo failed');
      toast.success(`Removed ${data.removed} ${batch.kind} row(s) from ${batch.project_id}.`);
      fetchHistory();
    } catch (e) {
      toast.error(`Undo failed: ${e.message}`);
    } finally {
      setUndoing(null);
    }
  };

  const cols = kind === 'labor' ? LABOR_COLS : kind === 'expense' ? EXPENSE_COLS : NOTES_COLS;

  return (
    <Layout>
      <div className="p-8 max-w-6xl">
        <h2 className="text-2xl text-[#5B8DB8] font-light mb-1">Setup</h2>
        <h1 className="text-3xl text-[#5B8DB8] font-light mb-6">Import Labor &amp; Expenses from Screenshot</h1>

        <p className="text-sm text-gray-600 mb-6">
          Upload a Labor Summary, Project Expenses, or Project Notes screenshot from the old site.
          The server reads it with Gemini 3 Pro vision, detects the Project #, and lets
          you confirm every row before committing. Multiple screenshots can be uploaded
          at once — all rows are aggregated for the same project.
        </p>

        {/* Kind toggle */}
        <div className="bg-white rounded shadow p-4 mb-6 flex items-center gap-3">
          <span className="text-sm text-gray-600 mr-2">Type:</span>
          {['labor', 'expense', 'notes'].map((k) => (
            <button
              key={k}
              onClick={() => handleKindChange(k)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                kind === k
                  ? 'bg-[#4ECFDB] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`ocr-kind-${k}`}
            >
              {k === 'labor' ? 'Labor Summary' : k === 'expense' ? 'Project Expenses' : 'Notes'}
            </button>
          ))}
        </div>

        {/* Upload */}
        <div className="bg-white rounded shadow p-6 mb-6">
          {/* Optional: lock all uploaded screenshots to one project */}
          <div className="mb-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="ocr-lock-project-row">
            <div>
              <label className="block text-xs text-gray-600 uppercase mb-1">
                Project # <span className="text-gray-400 normal-case">(optional — lock all screenshots to this project)</span>
              </label>
              <input
                type="text"
                value={projectId}
                onChange={(e) => checkProject(e.target.value.trim())}
                placeholder="e.g. R26-9207-1"
                className={`border rounded px-3 py-2 text-sm w-56 ${
                  projectExists === false ? 'border-red-400' : 'border-gray-300'
                }`}
                data-testid="ocr-lock-project-id"
              />
              <div className="text-xs mt-1 h-4">
                {projectExists === true && <span className="text-green-600">✓ Locked to {projectId} — every uploaded screenshot will import into this project.</span>}
                {projectExists === false && projectId && <span className="text-red-600">Project not found</span>}
                {!projectId && <span className="text-gray-400">Leave blank to auto-detect from the screenshot.</span>}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={onFileInputChange}
              className="text-sm"
              data-testid="ocr-file-input"
            />
            <label
              htmlFor="ocr-paste-input"
              className="border-2 border-dashed border-gray-300 hover:border-[#4ECFDB] focus-within:border-[#4ECFDB] focus-within:bg-[#4ECFDB]/5 rounded px-4 py-2 text-sm text-gray-600 cursor-text inline-flex items-center gap-1 transition-colors"
              title="Click here, then press Ctrl/Cmd+V to paste a screenshot from your clipboard"
              data-testid="ocr-paste-zone"
            >
              <span>📋 …or paste screenshots here</span>
              <input
                id="ocr-paste-input"
                type="text"
                readOnly
                aria-label="Paste a screenshot here with Ctrl or Cmd plus V"
                className="bg-transparent border-0 outline-none w-3 caret-[#4ECFDB] text-transparent cursor-text p-0"
                data-testid="ocr-paste-zone-input"
              />
              <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">Ctrl/⌘+V</kbd>
            </label>
            <button
              onClick={handleParse}
              disabled={files.length === 0 || parsing}
              className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="ocr-parse-btn"
            >
              {parsing
                ? `Reading ${parseProgress.done}/${parseProgress.total}…`
                : files.length > 1
                  ? `Parse ${files.length} Screenshots`
                  : 'Parse Screenshot'}
            </button>
            {(files.length > 0 || rows.length > 0) && (
              <button
                onClick={reset}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
                data-testid="ocr-reset-btn"
              >
                Reset
              </button>
            )}
          </div>

          {/* Staged files queue */}
          {files.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="ocr-file-queue">
              {files.map((f, idx) => (
                <div
                  key={`${f.name}-${idx}`}
                  className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-xs text-gray-700"
                  data-testid={`ocr-file-chip-${idx}`}
                >
                  <span>📷</span>
                  <span className="max-w-[220px] truncate" title={f.name}>{f.name}</span>
                  <span className="text-gray-400">({Math.round(f.size / 1024)} KB)</span>
                  <button
                    onClick={() => removeFile(idx)}
                    className="text-red-500 hover:text-red-700 ml-1"
                    aria-label="Remove screenshot"
                    data-testid={`ocr-file-chip-${idx}-remove`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview / edit */}
        {rows.length > 0 && (
          <div className="bg-white rounded shadow p-6 mb-6" data-testid="ocr-preview-card">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-4">
              <div>
                <label className="block text-xs text-gray-600 uppercase mb-1">Project #</label>
                <input
                  type="text"
                  value={projectId}
                  onChange={(e) => checkProject(e.target.value.trim())}
                  className={`border rounded px-3 py-2 text-sm w-44 ${
                    projectExists === false ? 'border-red-400' : 'border-gray-300'
                  }`}
                  data-testid="ocr-project-id"
                />
                <div className="text-xs mt-1 h-4">
                  {projectExists === true && <span className="text-green-600">✓ Project found</span>}
                  {projectExists === false && <span className="text-red-600">Project not found</span>}
                </div>
              </div>
              {kind === 'labor' && (
                <button
                  onClick={() => {
                    const today = todayISO();
                    setRows((prev) => prev.map((r) => ({ ...r, date: today })));
                    toast.success('Set every row to today.');
                  }}
                  className="border border-gray-300 hover:bg-gray-100 text-gray-700 px-3 py-2 rounded text-xs h-fit"
                  data-testid="ocr-set-all-dates-today"
                  title="Set the Date column on every row to today"
                >
                  Set all dates to today
                </button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={addRow}
                  className="border border-gray-300 hover:bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm"
                  data-testid="ocr-add-row"
                >
                  + Add row
                </button>
                <button
                  onClick={handleCommit}
                  disabled={committing || projectExists === false || !projectId.trim()}
                  className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="ocr-commit-btn"
                >
                  {committing ? 'Saving…' : `Commit to ${projectId || 'project'}`}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2">
                    {cols.map((c) => (
                      <th key={c.key} className="text-left p-2 text-gray-600 font-normal">{c.label}</th>
                    ))}
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} className="border-b align-top">
                      {cols.map((c) => (
                        <td key={c.key} className={`p-2 ${c.width}`}>
                          {c.type === 'checkbox' ? (
                            <input
                              type="checkbox"
                              checked={Boolean(row[c.key])}
                              onChange={(e) => updateRow(idx, c.key, e.target.checked)}
                              className="w-4 h-4"
                              data-testid={`ocr-row-${idx}-${c.key}`}
                            />
                          ) : (
                            <input
                              type={c.type === 'number' ? 'number' : c.type === 'date' ? 'date' : 'text'}
                              step={c.type === 'number' ? '0.01' : undefined}
                              value={row[c.key] ?? ''}
                              onChange={(e) => updateRow(idx, c.key, e.target.value)}
                              className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                              data-testid={`ocr-row-${idx}-${c.key}`}
                            />
                          )}
                        </td>
                      ))}
                      <td className="p-2 text-center">
                        <button
                          onClick={() => removeRow(idx)}
                          className="text-red-500 hover:text-red-700 text-lg"
                          aria-label="Remove row"
                          data-testid={`ocr-row-${idx}-remove`}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {rows.length > 0 && cols.some((c) => c.type === 'number') && (
                  <tfoot>
                    <tr className="border-t-2 font-medium bg-gray-50">
                      {cols.map((c) => {
                        // Sums roll up only numeric columns. Non-numeric cells
                        // get a "Totals" label (first one) or stay blank.
                        if (c.type !== 'number') {
                          const isFirst = cols.findIndex((x) => x.type !== 'number') === cols.indexOf(c);
                          return (
                            <td key={c.key} className={`p-2 ${c.width}`}>
                              {isFirst ? <span className="text-sm text-gray-700">Totals</span> : null}
                            </td>
                          );
                        }
                        const sum = rows.reduce((s, r) => s + (parseFloat(r[c.key]) || 0), 0);
                        const overridden = totalsOverride[c.key];
                        const display = overridden !== undefined && overridden !== null && overridden !== ''
                          ? overridden
                          : (sum ? sum.toFixed(2) : '0.00');
                        return (
                          <td key={c.key} className={`p-2 ${c.width}`}>
                            <input
                              type="text"
                              value={display}
                              onChange={(e) => setTotalsOverride((prev) => ({ ...prev, [c.key]: e.target.value }))}
                              className="border border-gray-300 rounded px-2 py-1 text-sm w-full font-medium"
                              data-testid={`ocr-totals-${c.key}`}
                            />
                          </td>
                        );
                      })}
                      <td className="p-2"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* History panel */}
        <div className="bg-white rounded shadow p-6 mb-6" data-testid="ocr-history-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg text-[#5B8DB8] font-light">Recent Imports</h2>
            <button
              onClick={fetchHistory}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
              data-testid="ocr-history-refresh"
            >
              Refresh
            </button>
          </div>
          {history.length === 0 ? (
            <div className="text-sm text-gray-500 italic py-4">No imports yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left p-2 text-gray-600 font-normal">When</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Kind</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Project #</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Rows</th>
                    <th className="text-left p-2 text-gray-600 font-normal">By</th>
                    <th className="text-right p-2 text-gray-600 font-normal w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((b) => {
                    const when = b.committed_at ? new Date(b.committed_at).toLocaleString() : '';
                    return (
                      <tr key={b.batch_id} className="border-b align-top" data-testid={`ocr-history-row-${b.batch_id}`}>
                        <td className="p-2 text-gray-700 whitespace-nowrap">{when}</td>
                        <td className="p-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                            b.kind === 'labor' ? 'bg-blue-100 text-blue-700' : b.kind === 'expense' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {b.kind === 'labor' ? 'Labor' : b.kind === 'expense' ? 'Expense' : 'Notes'}
                          </span>
                        </td>
                        <td className="p-2 font-medium">
                          <Link
                            to={`/projects/${encodeURIComponent(b.project_id)}`}
                            className="text-[#5B8DB8] hover:underline"
                            data-testid={`ocr-history-project-link-${b.batch_id}`}
                          >
                            {b.project_id}
                          </Link>
                        </td>
                        <td className="p-2 text-gray-700">{b.row_count}</td>
                        <td className="p-2 text-gray-700">{b.committed_by || '—'}</td>
                        <td className="p-2 text-right">
                          <button
                            onClick={() => handleUndo(b)}
                            disabled={undoing === b.batch_id}
                            className="text-red-600 hover:text-red-800 text-xs underline disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`ocr-history-undo-${b.batch_id}`}
                          >
                            {undoing === b.batch_id ? 'Undoing…' : 'Undo'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ImportLaborExpenses;
